/**
 * Date utility functions for Vertretungsplanner
 * Extracted from Figma Vertretungs planner/src/App.tsx
 */

/**
 * Formats a Date object to German date format (DD.MM.YYYY)
 */
export const formatDate = (date: Date): string => {
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Converts German date format (DD.MM.YYYY) to ISO format (YYYY-MM-DD)
 */
export const formatDateToISO = (dateString: string): string => {
  const [day, month, year] = dateString.split('.');
  return `${year}-${month}-${day}`;
};

/**
 * Converts Date object directly to ISO format (YYYY-MM-DD)
 */
export const formatDateObjectToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Returns ISO date (YYYY-MM-DD) for the given moment in Europe/Berlin timezone
 */
export const getBerlinDateISO = (at: Date = new Date()): string => {
  const parts = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(at);
  const year = parts.find(p => p.type === 'year')?.value || '0000';
  const month = parts.find(p => p.type === 'month')?.value || '01';
  const day = parts.find(p => p.type === 'day')?.value || '01';
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date or ISO string to HH:MM in Europe/Berlin timezone
 */
export const parseDbTimestampToDate = (s: string): Date => {
  const withT = s.includes('T') ? s : s.replace(' ', 'T');
  const hasTZ = /[zZ]|[+-]\d{2}:\d{2}$/.test(withT);
  return new Date(hasTZ ? withT : `${withT}Z`);
};

export const formatTimeBerlin = (value: Date | string): string => {
  const d = typeof value === 'string' ? parseDbTimestampToDate(value) : value;
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(d);
};

/**
 * Converts ISO date format (YYYY-MM-DD) back to German format (DD.MM.YYYY)
 */
export const formatDateFromISO = (isoString: string): string => {
  const date = new Date(isoString);
  return formatDate(date);
};

/**
 * Formats a Date object to full German date format with weekday
 */
export const formatFullDate = (date: Date): string => {
  return date.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Capitalizes the first letter of a string
 */
export const capitalizeType = (type: string): string => {
  return type.charAt(0).toUpperCase() + type.slice(1);
};

/**
 * Checks if a given date falls within an absence period
 * Handles both single-day and multi-day absences
 * 
 * @param entryDate - Start date of absence in DD.MM.YYYY format
 * @param entryStatus - Status string that may contain end date (e.g., "ganzt��gig abwesend bis 02.09.2025")
 * @param currentDate - Date object to check against
 * @returns true if currentDate falls within the absence period
 */
export const isDateInAbsencePeriod = (entryDate: string, entryStatus: string, currentDate: Date): boolean => {
  const currentDateString = formatDate(currentDate);

  // Helper to parse DD.MM.YYYY -> Date
  const parseDE = (s: string): Date | null => {
    const parts = s.split('.');
    if (parts.length !== 3) return null;
    const [d, m, y] = parts;
    const dd = parseInt(d, 10), mm = parseInt(m, 10), yy = parseInt(y, 10);
    if (!dd || !mm || !yy) return null;
    return new Date(yy, mm - 1, dd);
  };

  // Helper to parse YYYY-MM-DD -> Date
  const parseISO = (s: string): Date | null => {
    const parts = s.split('-');
    if (parts.length !== 3) return null;
    const [y, m, d] = parts;
    const yy = parseInt(y, 10), mm = parseInt(m, 10), dd = parseInt(d, 10);
    if (!dd || !mm || !yy) return null;
    return new Date(yy, mm - 1, dd);
  };

  // Multi-day: accept both "bis DD.MM.YYYY" and "bis YYYY-MM-DD"
  if (entryStatus.includes('bis')) {
    let endDate: Date | null = null;
    const deMatch = entryStatus.match(/bis (\d{2}\.\d{2}\.\d{4})/);
    const isoMatch = entryStatus.match(/bis (\d{4}-\d{2}-\d{2})/);
    if (deMatch) endDate = parseDE(deMatch[1]);
    else if (isoMatch) endDate = parseISO(isoMatch[1]);

    const startDate = parseDE(entryDate);
    const [currentDay, currentMonth, currentYear] = currentDateString.split('.');
    const checkDate = new Date(parseInt(currentYear), parseInt(currentMonth) - 1, parseInt(currentDay));

    if (startDate && endDate) {
      return checkDate >= startDate && checkDate <= endDate;
    }
  }

  // Single-day: dates must match exactly (DD.MM.YYYY)
  return entryDate === currentDateString;
};

/**
 * Converts a day number (1-5) to its German short label (Mo-Fr)
 */
export const dayNumberToLabel = (d: number): string => {
  switch (d) {
    case 1: return 'Mo';
    case 2: return 'Di';
    case 3: return 'Mi';
    case 4: return 'Do';
    case 5: return 'Fr';
    default: return String(d);
  }
};
